<template>
  <div class="customer-pool" v-loading="loading">
    <!-- 顶部状态条：开放窗口 / 我的容量 / 今日认领持有数 -->
    <el-row :gutter="16" class="status-bar">
      <el-col :span="8">
        <el-card shadow="never" class="status-card">
          <div class="status-label">认领开放状态</div>
          <div class="status-main">
            <el-tag v-if="ctx.window_open" type="success" size="medium" effect="dark">开放中</el-tag>
            <el-tag v-else type="info" size="medium">未开放</el-tag>
          </div>
          <div class="status-hint">
            <template v-if="!ctx.window_open && ctx.next_open_at">
              下次开放：{{ formatTime(ctx.next_open_at) }}
            </template>
            <template v-else>{{ openWindowText }}</template>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="status-card">
          <div class="status-label">我的客户容量</div>
          <div class="status-main">
            <span class="status-num" :class="{ 'is-warning': capacity.warning_reached }">
              {{ capacity.usage || 0 }}
            </span>
            <span class="status-sub"> / {{ capacity.limit || 0 }}</span>
          </div>
          <el-progress
            :percentage="capacityPercent"
            :status="capacity.warning_reached ? 'warning' : undefined"
            :show-text="false"
          />
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="status-card">
          <div class="status-label">今日认领持有</div>
          <div class="status-main">
            <span class="status-num">{{ ctx.daily_claimed || 0 }}</span>
            <span class="status-sub"> / {{ ctx.daily_limit || 0 }}</span>
          </div>
          <div class="status-hint">释放当天认领客户后可恢复名额</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 筛选区 -->
    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" size="small" @submit.native.prevent>
        <el-form-item label="关键词">
          <el-input
            v-model="filters.keyword"
            placeholder="客户名/电话/品牌等"
            clearable
            style="width: 200px"
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item label="客户等级">
          <el-select v-model="filters.customer_level_id_eq" placeholder="全部" clearable style="width: 140px">
            <el-option v-for="o in levelOptions" :key="o.id" :label="o.name" :value="o.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="客户来源">
          <el-select v-model="filters.customer_source_id_eq" placeholder="全部" clearable style="width: 140px">
            <el-option v-for="o in sourceOptions" :key="o.id" :label="o.name" :value="o.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="成交热度">
          <el-select v-model="filters.trade_heat_eq" placeholder="全部" clearable style="width: 140px">
            <el-option v-for="(label, value) in tradeHeatOptions" :key="value" :label="label" :value="value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 列表 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>公海客户（共 {{ pagination.total }} 个）</span>
      </div>

      <el-table
        :data="list"
        size="small"
        empty-text="暂无公海客户"
      >
        <el-table-column label="客户名" min-width="160">
          <template slot-scope="{ row }">
            <el-link type="primary" @click="goDetail(row)">{{ row.name }}</el-link>
          </template>
        </el-table-column>
        <el-table-column label="等级" width="100">
          <template slot-scope="{ row }">{{ (row.customer_level && row.customer_level.name) || '-' }}</template>
        </el-table-column>
        <el-table-column label="来源" width="120">
          <template slot-scope="{ row }">{{ (row.customer_source && row.customer_source.name) || '-' }}</template>
        </el-table-column>
        <el-table-column label="成交热度" width="100">
          <template slot-scope="{ row }">{{ tradeHeatOptions[row.trade_heat] || '-' }}</template>
        </el-table-column>
        <el-table-column label="原归属人" width="110">
          <template slot-scope="{ row }">{{ (row.pool_released_user && row.pool_released_user.screen_name) || '-' }}</template>
        </el-table-column>
        <el-table-column label="近30天下单" width="110" align="right">
          <template slot-scope="{ row }">{{ row.last_30_day_order_qty || 0 }}</template>
        </el-table-column>
        <el-table-column label="最近有效跟进" width="150">
          <template slot-scope="{ row }">{{ row.latest_effective_followed_at ? formatTime(row.latest_effective_followed_at) : '-' }}</template>
        </el-table-column>
        <el-table-column label="最近成交" width="120">
          <template slot-scope="{ row }">{{ row.latest_ordered_on ? formatDate(row.latest_ordered_on) : '-' }}</template>
        </el-table-column>
        <el-table-column label="进池时间" width="160">
          <template slot-scope="{ row }">{{ row.pool_entered_at ? formatTime(row.pool_entered_at) : '-' }}</template>
        </el-table-column>
        <el-table-column label="进池天数" width="90" align="right">
          <template slot-scope="{ row }">{{ row.pool_days || 0 }}</template>
        </el-table-column>
        <el-table-column label="释放原因" min-width="140">
          <template slot-scope="{ row }">{{ row.pool_released_reason || '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <el-button
                type="primary"
                size="mini"
                :disabled="!row.can_claim"
                @click="claim(row)"
              >
                领取
              </el-button>
              <el-button
                v-if="row.can_pool_assign"
                type="primary"
                size="mini"
                @click="openAssign(row)"
              >
                分配
              </el-button>
              <el-button size="mini" @click="goDetail(row)">详情</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next, sizes"
        :total="pagination.total"
        :current-page="pagination.page"
        :page-size="pagination.perPage"
        :page-sizes="[20, 50, 100]"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </el-card>

    <el-dialog
      title="分配公海客户"
      :visible.sync="assignDialogVisible"
      width="420px"
      @closed="resetAssignDialog"
    >
      <el-form size="small" label-width="80px" @submit.native.prevent>
        <el-form-item label="客户">
          <span class="assign-customer-name">{{ assigningRow ? assigningRow.name : '-' }}</span>
        </el-form-item>
        <el-form-item label="分配给">
          <el-select
            v-model="assignForm.user_id"
            filterable
            remote
            reserve-keyword
            placeholder="搜索用户"
            :remote-method="searchAssignUsers"
            :loading="assignUserLoading"
            style="width: 100%"
          >
            <el-option
              v-for="user in assignUserOptions"
              :key="user.id"
              :label="displayUser(user)"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="assignDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="assignSubmitting"
          @click="submitAssign"
        >
          确定分配
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { request } from 'beans-admin-plugin';
import moment from 'moment';
import { TRADE_HEAT } from '@/constants';

@Component
export default class CustomerPool extends Vue {
  loading = false;
  list = [];
  ctx = {};
  levelOptions = [];
  sourceOptions = [];
  tradeHeatOptions = TRADE_HEAT;
  assignDialogVisible = false;
  assigningRow = null;
  assignUserOptions = [];
  assignUserLoading = false;
  assignSubmitting = false;

  filters = {
    keyword: '',
    customer_level_id_eq: '',
    customer_source_id_eq: '',
    trade_heat_eq: '',
  };

  assignForm = {
    user_id: '',
  };

  pagination = {
    page: 1,
    perPage: 20,
    total: 0,
  };

  get capacity() {
    return this.ctx.capacity || {};
  }

  get capacityPercent() {
    const { usage_ratio } = this.capacity;
    return Math.min(Math.round((usage_ratio || 0) * 100), 100);
  }

  get openWindowText() {
    const days = this.ctx.open_days || [];
    const fromHour = this.ctx.open_from_hour;
    const toHour = this.ctx.open_to_hour;

    if (!days.length || fromHour === null || fromHour === undefined || toHour === null || toHour === undefined) {
      return '开放时间以系统配置为准';
    }

    return `每月 ${days.join('、')} 日 ${this.formatHour(fromHour)}-${this.formatHour(toHour)}`;
  }

  mounted() {
    this.fetchOptions();
    this.fetchContext();
    this.fetchList();
  }

  formatTime(value) {
    return moment(value).format('YYYY-MM-DD HH:mm');
  }

  formatDate(value) {
    return moment(value).format('YYYY-MM-DD');
  }

  formatHour(value) {
    return `${String(value).padStart(2, '0')}:00`;
  }

  displayUser(user) {
    return [user.screen_name || user.name, user.email].filter(Boolean).join(' / ') || `用户#${user.id}`;
  }

  async fetchOptions() {
    try {
      const [levels, sources] = await Promise.all([
        request.get('/customer_levels/read_options', { per_page: 100 }),
        request.get('/customer_sources/read_options', { per_page: 100 }),
      ]);
      this.levelOptions = levels.data || [];
      this.sourceOptions = sources.data || [];
    } catch (e) {
      // 选项加载失败不阻断主流程
    }
  }

  async fetchContext() {
    try {
      const ctx = await request.get('/customers/pool_context');
      this.ctx = ctx || {};
    } catch (e) {
      this.ctx = {};
    }
  }

  async fetchList() {
    this.loading = true;
    try {
      const params = {
        user_id_null: true,
        page: this.pagination.page,
        per_page: this.pagination.perPage,
      };
      Object.keys(this.filters).forEach((key) => {
        const val = this.filters[key];
        if (val !== '' && val !== null && val !== undefined) {
          params[key] = val;
        }
      });
      const res = await request.get('/customers', params);
      this.list = res.data || [];
      this.pagination.total = (res.pagination && res.pagination.total) || this.list.length;
    } finally {
      this.loading = false;
    }
  }

  search() {
    this.pagination.page = 1;
    this.fetchList();
  }

  resetFilters() {
    this.filters = {
      keyword: '',
      customer_level_id_eq: '',
      customer_source_id_eq: '',
      trade_heat_eq: '',
    };
    this.search();
  }

  onPageChange(page) {
    this.pagination.page = page;
    this.fetchList();
  }

  onSizeChange(size) {
    this.pagination.perPage = size;
    this.pagination.page = 1;
    this.fetchList();
  }

  goDetail(row) {
    this.$router.push({ name: 'customers.show', params: { id: row.id } });
  }

  async claim(row) {
    try {
      await this.$confirm(`确定领取客户「${row.name}」?`, '领取确认');
    } catch (e) {
      return; // 用户取消
    }
    // 请求失败由全局 onError 统一提示，不再重复弹错
    await this.$autoLoading(request.put(`/customers/${row.id}/pick`));
    this.$message.success(this.$t('operateSuccess'));
    this.refresh();
  }

  openAssign(row) {
    this.assigningRow = row;
    this.assignForm = { user_id: '' };
    this.assignDialogVisible = true;
    this.searchAssignUsers();
  }

  async searchAssignUsers(keyword = '') {
    this.assignUserLoading = true;
    try {
      const params = { per_page: 100 };
      if (keyword) {
        params.keyword = keyword;
      }
      const res = await request.get('/users/read_options', params);
      this.assignUserOptions = res.data || [];
    } finally {
      this.assignUserLoading = false;
    }
  }

  resetAssignDialog() {
    this.assigningRow = null;
    this.assignForm = { user_id: '' };
    this.assignUserOptions = [];
    this.assignSubmitting = false;
  }

  async submitAssign() {
    if (!this.assigningRow) return;
    if (!this.assignForm.user_id) {
      this.$message.warning('请选择分配用户');
      return;
    }

    this.assignSubmitting = true;
    try {
      await this.$autoLoading(request.put(`/customers/${this.assigningRow.id}/assign`, this.assignForm));
      this.$message.success(this.$t('operateSuccess'));
      this.assignDialogVisible = false;
      this.refresh();
    } finally {
      this.assignSubmitting = false;
    }
  }

  refresh() {
    this.fetchContext();
    this.fetchList();
  }
}

export const route = [
  {
    path: '/customer_pool',
    name: 'customer_pool.index',
    component: CustomerPool,
  },
];
</script>

<style scoped>
.customer-pool {
  padding: 16px;
}
.status-bar {
  margin-bottom: 16px;
}
.status-card {
  min-height: 110px;
}
.status-label {
  color: #909399;
  font-size: 13px;
  margin-bottom: 8px;
}
.status-main {
  margin-bottom: 8px;
}
.status-num {
  font-size: 26px;
  font-weight: 600;
  color: #303133;
}
.status-num.is-warning {
  color: #e6a23c;
}
.status-sub {
  font-size: 14px;
  color: #909399;
}
.status-hint {
  color: #c0c4cc;
  font-size: 12px;
}
.filter-card {
  margin-bottom: 16px;
}
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.row-actions .el-button + .el-button {
  margin-left: 0;
}
.assign-customer-name {
  color: #303133;
  font-weight: 500;
}
</style>
